import { InfoAlert } from "@/components/shared/Alerts";
import { GenericTable } from "@/components/shared/GenericTable";
import { SelectSpecValuesTableToolbar } from "../SelectSpecValuesTableToolbar";
import { SelectedSpecValue, SpecValueCellWithMasterValue } from "../SpecValueCell";
import type { SpecList, SpecValue } from "@/types/specList";
import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dispatch, SetStateAction, useMemo } from "react";

type ModifedSpecListTableProps = {
  deviceClasses: string[];
  conditions: string[];
  specList: SpecList;
  displaySpecs: any;
  setShowMasterSpecs: (show: boolean) => void;
  showMasterSpecs: boolean;
  setSelectedSpecs: Dispatch<SetStateAction<SelectedSpecValue[]>>;
};

export function ModifiedSpecListTable({
  deviceClasses,
  conditions,
  specList,
  displaySpecs,
  setShowMasterSpecs,
  setSelectedSpecs,
  showMasterSpecs,
}: ModifedSpecListTableProps) {
  const addRowClassNames = (data: SelectedSpecValue) => {
    return data.isMasterValue ? "" : "bg-orange-50/50";
  };

  const showMasterSpecsToggle = specList.type === "derived";

  const handleSpecSelection = (specId: string, selected: boolean) => {
    setSelectedSpecs((prev) => {
      const existingSpec = prev.find((spec) => spec.id === specId);

      if (existingSpec) {
        return prev.map((spec) => (spec.id === specId ? { ...spec, selected } : spec));
      } else {
        const specFromDisplay = displaySpecs.find((spec: any) => spec.id === specId);
        if (specFromDisplay) {
          return [...prev, { ...specFromDisplay, selected }];
        }
        return prev;
      }
    });
  };

  const handleValueChange = (specId: string, field: keyof SpecValue, value: string) => {
    const numValue = value === "" ? undefined : Number.parseFloat(value);

    setSelectedSpecs((prev) => {
      const existingSpec = prev.find((spec) => spec.id === specId);

      if (existingSpec) {
        return prev.map((spec) => {
          if (spec.id === specId) {
            return {
              ...spec,
              customValues: {
                ...spec.customValues,
                [field]: numValue,
              },
            };
          }
          return spec;
        });
      } else {
        const specFromDisplay = displaySpecs.find((spec: any) => spec.id === specId);
        if (specFromDisplay) {
          return [
            ...prev,
            {
              ...specFromDisplay,
              customValues: {
                [field]: numValue,
              },
            },
          ];
        }
        return prev;
      }
    });
  };

  const syncFilteredRowsSelection = (table: any, checked: boolean) => {
    const filteredRows = table.getFilteredRowModel().rows;
    filteredRows.forEach((row: any) => {
      handleSpecSelection(row.original.id, checked); // form state
      row.toggleSelected(checked); // table state
    });
  };

  const columns: ColumnDef<SelectedSpecValue>[] = [
    {
      id: "select",
      header: ({ table }) => {
        const filteredRows = table.getFilteredRowModel().rows;
        const allFilteredSelected =
          filteredRows.length > 0 && filteredRows.every((row) => row.original.selected);
        const someFilteredSelected = filteredRows.some((row) => row.original.selected);

        return (
          <Checkbox
            checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
            onCheckedChange={(checked) => syncFilteredRowsSelection(table, !!checked)}
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          checked={row.original.selected}
          onCheckedChange={(checked) => {
            handleSpecSelection(row.original.id, !!checked);
            const toggleRow = row.getToggleSelectedHandler();
            return toggleRow(checked);
          }}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      id: "name",
      accessorKey: "spec.name",
      header: "Spec Name",
    },
    {
      id: "condition",
      accessorKey: "spec.condition",
      header: () => "Condition",
      enableGlobalFilter: false,
      cell: ({ row }) => <Badge variant="outline">{row.original.spec.condition}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "reportName",
      accessorKey: "spec.reportName",
      header: "Report Name",
    },
    {
      id: "legacyNames",
      accessorKey: "spec.legacyNames",
      header: "Legacy Name",
    },
    {
      id: "dutNames",
      accessorKey: "spec.dutNames",
      header: "DUT Name",
    },
    {
      id: "deviceClass",
      accessorKey: "spec.deviceClass",
      header: () => "Device Class",
      cell: ({ row }) => <Badge variant="outline">{row.original.spec.deviceClass}</Badge>,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "lsl",
      accessorKey: "lsl",
      header: "LSL",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "lsl", handleValueChange),
      enableSorting: false,
    },
    {
      id: "target",
      accessorKey: "tgt",
      header: "Target",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "tgt", handleValueChange),
      enableSorting: false,
    },
    {
      id: "usl",
      accessorKey: "usl",
      header: "USL",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "usl", handleValueChange),
      enableSorting: false,
    },
    {
      id: "jmpYmin",
      accessorKey: "jmpYmin",
      header: "JMP Y Min",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "jmpYmin", handleValueChange),
      enableSorting: false,
    },
    {
      id: "jmpYmax",
      accessorKey: "jmpYmax",
      header: "JMP Y Max",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "jmpYmax", handleValueChange),
      enableSorting: false,
    },
    {
      id: "jmpYinc",
      accessorKey: "jmpYinc",
      header: "JMP Y Inc",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "jmpYinc", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libMedian",
      accessorKey: "libMedian",
      header: "Lib Median",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "libMedian", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libMean",
      accessorKey: "libMean",
      header: "Lib Mean",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "libMean", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libStd",
      accessorKey: "libStd",
      header: "Lib Std",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "libStd", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libQ10",
      accessorKey: "libQ10",
      header: "Lib Q10",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "libQ10", handleValueChange),
      enableSorting: false,
    },
    {
      id: "libQ90",
      accessorKey: "libQ90",
      header: "Lib Q90",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "libQ90", handleValueChange),
    },
    {
      id: "libN",
      accessorKey: "libN",
      header: "Lib N",
      enableGlobalFilter: false,
      cell: ({ row }) => SpecValueCellWithMasterValue(row.original, "libN", handleValueChange),
      enableSorting: false,
    },
  ];

  const tableData = useMemo(
    () =>
      (displaySpecs ?? []).map((spec: any) => ({
        ...spec,
        selected: spec.selected ?? true, // default all rows selected
      })),
    [displaySpecs],
  );

  const initialRowSelection = useMemo(
    () =>
      Object.fromEntries(
        tableData.map((row: any, index: number) => [String(row.id ?? index), true]),
      ),
    [tableData],
  );

  return (
    <>
      <InfoAlert>
        To update values, please select a specification by clicking the checkbox.
      </InfoAlert>

      <div className="mt-4">
        <GenericTable
          key="table-with-master-specs"
          addRowClassNames={addRowClassNames}
          rowSelection={initialRowSelection}
          columns={columns}
          data={displaySpecs}
          toolbarComponent={(props) => (
            <SelectSpecValuesTableToolbar
              {...props}
              conditions={conditions}
              deviceClasses={deviceClasses}
              onShowMasterSpecsChange={setShowMasterSpecs}
              showMasterSpecs={showMasterSpecs}
              showMasterSpecsToggle={showMasterSpecsToggle}
            />
          )}
        />
      </div>
    </>
  );
}
